import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';

const API_URL = 'https://iia-backend-production.up.railway.app';

const AcademicResourceCenter = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('directory');

  const sections = [
    { id: 'directory', label: '👥 People Directory' },
    { id: 'messages', label: '✉️ Messages' },
    { id: 'events', label: '📅 Events Calendar' },
    { id: 'policies', label: '📋 Policies & Terms' },
    { id: 'documents', label: '📁 Document Center' },
    { id: 'signable', label: '✍️ Sign Documents' },
  ];

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h1 style={styles.logo}>IIA</h1>
          <span style={styles.logoText}>Impact Influence Academy</span>
        </div>
        <div style={styles.headerRight}>
          <span style={styles.welcome}>Welcome, {user?.first_name}</span>
          <button style={styles.returnBtn} onClick={() => navigate('/campus')}>
            ← Return to Campus Square
          </button>
        </div>
      </div>

      <div style={styles.main}>
        <div style={styles.pageHeader}>
          <h2 style={styles.pageTitle}>🏫 Academic Resource Center</h2>
          <p style={styles.pageSubtitle}>Directory, messaging, events, policies, and documents</p>
        </div>

        <div style={styles.tabs}>
          {sections.map(section => (
            <button
              key={section.id}
              style={activeSection === section.id ? styles.tabActive : styles.tab}
              onClick={() => setActiveSection(section.id)}
            >
              {section.label}
            </button>
          ))}
        </div>

        <div style={styles.content}>
          {activeSection === 'directory' && <PeopleDirectory user={user} navigate={navigate} />}
          {activeSection === 'messages' && <Messages user={user} />}
          {activeSection === 'events' && <EventsCalendar user={user} />}
          {activeSection === 'policies' && <PoliciesTerms user={user} />}
          {activeSection === 'documents' && <DocumentCenter user={user} />}
          {activeSection === 'signable' && <SignableDocuments user={user} />}
        </div>
      </div>
    </div>
  );
};

// ==================== SIGNABLE DOCUMENTS ====================

const SignableDocuments = ({ user }) => {
  const isAdmin = user?.role_id === 1;
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [signedName, setSignedName] = useState('');
  const [signing, setSigning] = useState(false);
  const [message, setMessage] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [modules, setModules] = useState([]);
  const [form, setForm] = useState({ title: '', content: '', required_before_module_id: '' });

  useEffect(() => {
    fetchDocuments();
    if (isAdmin) fetchModules();
  }, []);

  const fetchDocuments = async () => {
    try {
      const endpoint = isAdmin
        ? `${API_URL}/api/academic/signable-documents/admin`
        : `${API_URL}/api/academic/signable-documents`;
      const res = await axios.get(endpoint);
      setDocuments(res.data.documents);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchModules = async () => {
    try {
      // Fetch all units then modules for admin dropdown
      const res = await axios.get(`${API_URL}/api/instructional/courses/1/units`);
      const units = res.data.units || [];
      const allModules = [];
      for (const unit of units) {
        const modRes = await axios.get(`${API_URL}/api/instructional/units/${unit.id}/modules`);
        (modRes.data.modules || []).forEach(m => allModules.push({ ...m, unit_title: unit.title }));
      }
      setModules(allModules);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSign = async () => {
    if (!signedName.trim()) return;
    setSigning(true);
    console.log('Signing document ID:', selectedDoc?.id); // ADD THIS LINE
    try {
      await axios.post(`${API_URL}/api/academic/signable-documents/${selectedDoc.id}/sign`, {
        signed_name: signedName,
      });
      setMessage('✅ Document signed successfully!');
      setSignedName('');
      setSelectedDoc(null);
      fetchDocuments();
    } catch (err) {
      setMessage('Error signing document. Please try again.');
    } finally {
      setSigning(false);
    }
  };

  const handleCreate = async () => {
    if (!form.title || !form.content) {
      setMessage('Title and content are required.');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/academic/signable-documents`, {
        title: form.title,
        content: form.content,
        required_before_module_id: form.required_before_module_id || null,
      });
      setMessage('✅ Document created and assigned to all students.');
      setShowForm(false);
      setForm({ title: '', content: '', required_before_module_id: '' });
      fetchDocuments();
    } catch (err) {
      setMessage('Error creating document.');
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Delete this document? All signatures will be lost.')) return;
    try {
      await axios.delete(`${API_URL}/api/academic/signable-documents/${docId}`);
      fetchDocuments();
    } catch (err) {
      console.error(err);
    }
  };

  const unsigned = documents.filter(d => !d.signature_id);
  const signed = documents.filter(d => d.signature_id);

  if (loading) return <p style={styles.loadingText}>Loading documents...</p>;

  return (
    <div>
      {message && (
        <div style={message.startsWith('✅') ? styles.successBanner : styles.errorBanner}>
          {message}
        </div>
      )}

      {/* ADMIN VIEW */}
      {isAdmin && (
        <div>
          <div style={styles.calendarActions}>
            <button style={styles.addBtn} onClick={() => setShowForm(!showForm)}>
              + Create Signable Document
            </button>
          </div>

          {showForm && (
            <div style={styles.eventForm}>
              <h3 style={styles.sectionTitle}>New Signable Document</h3>
              <input
                style={styles.input}
                placeholder="Document title"
                value={form.title}
                onChange={e => setForm({ ...form, title: e.target.value })}
              />
              <textarea
                style={styles.textarea}
                placeholder="Document content (the text the student will read and sign)"
                value={form.content}
                onChange={e => setForm({ ...form, content: e.target.value })}
                rows={10}
              />
              <div>
                <label style={styles.fieldLabel}>Required before module (optional)</label>
                <select
                  style={styles.select}
                  value={form.required_before_module_id}
                  onChange={e => setForm({ ...form, required_before_module_id: e.target.value })}
                >
                  <option value="">— No gate, informational only —</option>
                  {modules.map(m => (
                    <option key={m.id} value={m.id}>
                      {m.unit_title} → {m.title}
                    </option>
                  ))}
                </select>
              </div>
              <p style={styles.helpText}>
                This document will be assigned to all current students automatically.
              </p>
              <div style={styles.formActions}>
                <button style={styles.saveBtn} onClick={handleCreate}>Create & Assign</button>
                <button style={styles.cancelBtn} onClick={() => setShowForm(false)}>Cancel</button>
              </div>
            </div>
          )}

          <h3 style={styles.sectionTitleGold}>All Signable Documents</h3>
          {documents.length === 0 && (
            <p style={styles.emptyText}>No signable documents created yet.</p>
          )}
          {documents.map(doc => (
            <div key={doc.id} style={styles.adminDocCard}>
              <div style={styles.adminDocLeft}>
                <h4 style={styles.adminDocTitle}>{doc.title}</h4>
                {doc.required_before_module_title && (
                  <p style={styles.adminDocGate}>
                    🔒 Gates: {doc.required_before_module_title}
                  </p>
                )}
                <p style={styles.adminDocStats}>
                  ✍️ {doc.signature_count} / {doc.total_students} students signed
                </p>
              </div>
              <button style={styles.deleteBtnSmall} onClick={() => handleDelete(doc.id)}>
                Delete
              </button>
            </div>
          ))}
        </div>
      )}

      {/* STUDENT VIEW */}
      {!isAdmin && (
        <div>
          {/* Unsigned documents */}
          {unsigned.length > 0 && (
            <div style={styles.unsignedSection}>
              <h3 style={styles.unsignedTitle}>⚠️ Requires Your Signature</h3>
              <p style={styles.unsignedSubtitle}>
                These documents must be signed to unlock gated content.
              </p>
              {unsigned.map(doc => (
                <div key={doc.id} style={styles.unsignedCard}>
                  <div style={styles.unsignedHeader}>
                    <h4 style={styles.unsignedDocTitle}>{doc.title}</h4>
                    {doc.required_before_module_title && (
                      <span style={styles.gateTag}>
                        🔒 Required before: {doc.required_before_module_title}
                      </span>
                    )}
                  </div>
                  <div style={styles.docContentBox}>
                    {doc.content.split('\n').map((line, i) => (
                      <p key={i} style={styles.docContentLine}>{line}</p>
                    ))}
                  </div>
                  {selectedDoc?.id === doc.id ? (
                    <div style={styles.signArea}>
                      <p style={styles.signInstruction}>
                        By typing your full legal name below, you agree to the terms above.
                      </p>
                      <input
                        style={styles.signInput}
                        placeholder="Type your full name to sign"
                        value={signedName}
                        onChange={e => setSignedName(e.target.value)}
                      />
                      <div style={styles.formActions}>
                        <button
                          style={signing ? styles.saveBtnDisabled : styles.saveBtn}
                          onClick={handleSign}
                          disabled={signing}
                        >
                          {signing ? 'Signing...' : '✍️ Sign Document'}
                        </button>
                        <button style={styles.cancelBtn} onClick={() => setSelectedDoc(null)}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button style={styles.signBtn} onClick={() => setSelectedDoc(doc)}>
                      ✍️ Read & Sign
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Signed documents */}
          {signed.length > 0 && (
            <div style={styles.signedSection}>
              <h3 style={styles.signedTitle}>✅ Signed Documents</h3>
              {signed.map(doc => (
                <div key={doc.id} style={styles.signedCard}>
                  <div style={styles.signedLeft}>
                    <span style={styles.signedIcon}>✅</span>
                    <div>
                      <h4 style={styles.signedDocTitle}>{doc.title}</h4>
                      <p style={styles.signedMeta}>
                        Signed as "{doc.signed_name}" on{' '}
                        {new Date(doc.signed_at).toLocaleDateString('en-US', {
                          year: 'numeric', month: 'long', day: 'numeric',
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {unsigned.length === 0 && signed.length === 0 && (
            <div style={styles.emptyState}>
              <p style={styles.emptyText}>No documents require your signature at this time.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ==================== PEOPLE DIRECTORY ====================

const PeopleDirectory = ({ user, navigate }) => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({ title: '', bio: '', photo_url: '', is_visible: true });
  const [message, setMessage] = useState('');
  const isStaff = user?.role_id <= 3;

  useEffect(() => { fetchProfiles(); }, []);

  const fetchProfiles = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/academic/profiles`);
      setProfiles(res.data.profiles);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyProfile = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/academic/profiles/me`);
      if (res.data.profile) {
        setForm({
          title: res.data.profile.title || '',
          bio: res.data.profile.bio || '',
          photo_url: res.data.profile.photo_url || '',
          is_visible: res.data.profile.is_visible ?? true,
        });
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleSaveProfile = async () => {
    try {
      await axios.post(`${API_URL}/api/academic/profiles`, form);
      setMessage('Profile saved');
      setEditMode(false);
      fetchProfiles();
    } catch (err) {
      setMessage('Error saving profile');
    }
  };

  const handleDM = async (staffId) => {
    try {
      const res = await axios.post(`${API_URL}/api/academic/conversations`, { staff_id: staffId });
      navigate(`/academic/messages/${res.data.conversation.id}`);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div>
      {isStaff && (
        <div style={styles.profileEditorCard}>
          <div style={styles.profileEditorHeader}>
            <h3 style={styles.sectionTitle}>Your Directory Profile</h3>
            <button style={styles.editBtn} onClick={() => { setEditMode(!editMode); if (!editMode) fetchMyProfile(); }}>
              {editMode ? 'Cancel' : 'Edit Profile'}
            </button>
          </div>
          {message && <p style={styles.successText}>{message}</p>}
          {editMode && (
            <div style={styles.profileForm}>
              <input style={styles.input} placeholder="Your title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} />
              <textarea style={styles.textarea} placeholder="Your bio" value={form.bio} onChange={e => setForm({...form, bio: e.target.value})} rows={4} />
              <input style={styles.input} placeholder="Photo URL (optional)" value={form.photo_url} onChange={e => setForm({...form, photo_url: e.target.value})} />
              <label style={styles.checkboxLabel}>
                <input type="checkbox" checked={form.is_visible} onChange={e => setForm({...form, is_visible: e.target.checked})} />
                <span style={styles.checkboxText}>Visible in directory</span>
              </label>
              <button style={styles.saveBtn} onClick={handleSaveProfile}>Save Profile</button>
            </div>
          )}
        </div>
      )}
      {loading && <p style={styles.loadingText}>Loading directory...</p>}
      {!loading && profiles.length === 0 && (
        <div style={styles.emptyState}>
          <p style={styles.emptyText}>No staff profiles yet.</p>
        </div>
      )}
      <div style={styles.profilesGrid}>
        {profiles.map(profile => (
          <div key={profile.id} style={styles.profileCard}>
            <div style={styles.profilePhoto}>
              {profile.photo_url ? (
                <img src={profile.photo_url} alt={profile.first_name} style={styles.photoImg} />
              ) : (
                <div style={styles.photoPlaceholder}>
                  {profile.first_name?.[0]}{profile.last_name?.[0]}
                </div>
              )}
            </div>
            <div style={styles.profileInfo}>
              <h4 style={styles.profileName}>{profile.first_name} {profile.last_name}</h4>
              <p style={styles.profileTitle}>{profile.title}</p>
              <p style={styles.profileRole}>{profile.role_name}</p>
              {profile.bio && <p style={styles.profileBio}>{profile.bio}</p>}
              {user?.role_id === 4 && (
                <button style={styles.dmBtn} onClick={() => handleDM(profile.user_id)}>
                  ✉️ Send Message
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ==================== MESSAGES ====================

const Messages = ({ user }) => {
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchConversations(); }, []);
  useEffect(() => { if (activeConv) fetchMessages(activeConv.id); }, [activeConv]);

  const fetchConversations = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/academic/conversations`);
      setConversations(res.data.conversations);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (convId) => {
    try {
      const res = await axios.get(`${API_URL}/api/academic/conversations/${convId}/messages`);
      setMessages(res.data.messages);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSend = async () => {
    if (!newMessage.trim()) return;
    try {
      await axios.post(`${API_URL}/api/academic/conversations/${activeConv.id}/messages`, { body: newMessage });
      setNewMessage('');
      fetchMessages(activeConv.id);
    } catch (err) {
      console.error(err);
    }
  };

  const getOtherName = (conv) => {
    if (user?.role_id === 4) return `${conv.staff_first} ${conv.staff_last}`;
    return `${conv.student_first} ${conv.student_last}`;
  };

  return (
    <div style={styles.messagesContainer}>
      <div style={styles.convList}>
        <h3 style={styles.convListTitle}>Conversations</h3>
        {loading && <p style={styles.loadingText}>Loading...</p>}
        {!loading && conversations.length === 0 && (
          <p style={styles.emptyTextSmall}>No conversations yet. Message a staff member from the directory.</p>
        )}
        {conversations.map(conv => (
          <div
            key={conv.id}
            style={activeConv?.id === conv.id ? styles.convItemActive : styles.convItem}
            onClick={() => setActiveConv(conv)}
          >
            <p style={styles.convName}>{getOtherName(conv)}</p>
            {conv.last_message && <p style={styles.convPreview}>{conv.last_message}</p>}
            {conv.unread_count > 0 && <span style={styles.unreadBadge}>{conv.unread_count}</span>}
          </div>
        ))}
      </div>
      <div style={styles.messageThread}>
        {!activeConv && (
          <div style={styles.noConvSelected}>
            <p style={styles.noConvText}>Select a conversation to view messages</p>
          </div>
        )}
        {activeConv && (
          <>
            <div style={styles.threadHeader}>
              <h4 style={styles.threadName}>{getOtherName(activeConv)}</h4>
            </div>
            <div style={styles.messagesList}>
              {messages.map(msg => (
                <div key={msg.id} style={msg.sender_id === user?.id ? styles.messageSent : styles.messageReceived}>
                  <p style={styles.messageBody}>{msg.body}</p>
                  <p style={styles.messageTime}>
                    {new Date(msg.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                  </p>
                </div>
              ))}
            </div>
            <div style={styles.messageInput}>
              <input
                style={styles.messageField}
                placeholder="Type a message..."
                value={newMessage}
                onChange={e => setNewMessage(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSend()}
              />
              <button style={styles.sendBtn} onClick={handleSend}>Send</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ==================== EVENTS CALENDAR ====================

const EventsCalendar = ({ user }) => {
  const [events, setEvents] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', event_date: '', end_date: '', location: '', link: '' });
  const [message, setMessage] = useState('');
  const isAdmin = user?.role_id <= 3;

  useEffect(() => { fetchEvents(); }, []);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/academic/events`);
      setEvents(res.data.events);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCreateEvent = async () => {
    try {
      await axios.post(`${API_URL}/api/academic/events`, form);
      setMessage('Event created');
      setShowForm(false);
      setForm({ title: '', description: '', event_date: '', end_date: '', location: '', link: '' });
      fetchEvents();
    } catch (err) {
      setMessage('Error creating event');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await axios.delete(`${API_URL}/api/academic/events/${eventId}`);
      setSelectedEvent(null);
      fetchEvents();
    } catch (err) {
      console.error(err);
    }
  };

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthName = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  const getEventsForDay = (day) => {
    return events.filter(event => {
      const d = new Date(event.event_date);
      return d.getFullYear() === year && d.getMonth() === month && d.getDate() === day;
    });
  };

  const days = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let i = 1; i <= daysInMonth; i++) days.push(i);

  return (
    <div>
      {isAdmin && (
        <div style={styles.calendarActions}>
          <button style={styles.addBtn} onClick={() => setShowForm(!showForm)}>+ Add Event</button>
        </div>
      )}
      {message && <p style={styles.successText}>{message}</p>}
      {showForm && (
        <div style={styles.eventForm}>
          <input style={styles.input} placeholder="Event title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} />
          <textarea style={styles.textarea} placeholder="Description" value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3} />
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.fieldLabel}>Start Date & Time</label>
              <input style={styles.input} type="datetime-local" value={form.event_date} onChange={e => setForm({...form, event_date: e.target.value})} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.fieldLabel}>End Date & Time</label>
              <input style={styles.input} type="datetime-local" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})} />
            </div>
          </div>
          <input style={styles.input} placeholder="Location (optional)" value={form.location} onChange={e => setForm({...form, location: e.target.value})} />
          <input style={styles.input} placeholder="Link (Zoom URL, etc.)" value={form.link} onChange={e => setForm({...form, link: e.target.value})} />
          <div style={styles.formActions}>
            <button style={styles.saveBtn} onClick={handleCreateEvent}>Save Event</button>
            <button style={styles.cancelBtn} onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </div>
      )}
      <div style={styles.calendar}>
        <div style={styles.calendarHeader}>
          <button style={styles.calNavBtn} onClick={() => setCurrentDate(new Date(year, month - 1))}>←</button>
          <h3 style={styles.monthTitle}>{monthName}</h3>
          <button style={styles.calNavBtn} onClick={() => setCurrentDate(new Date(year, month + 1))}>→</button>
        </div>
        <div style={styles.calendarGrid}>
          {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
            <div key={d} style={styles.dayHeader}>{d}</div>
          ))}
          {days.map((day, index) => (
            <div key={index} style={day ? styles.dayCell : styles.emptyCell}>
              {day && (
                <>
                  <span style={styles.dayNumber}>{day}</span>
                  {getEventsForDay(day).map(event => (
                    <div key={event.id} style={styles.eventDot} onClick={() => setSelectedEvent(event)}>
                      {event.title}
                    </div>
                  ))}
                </>
              )}
            </div>
          ))}
        </div>
      </div>
      {selectedEvent && (
        <div style={styles.modalOverlay} onClick={() => setSelectedEvent(null)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>{selectedEvent.title}</h3>
            <p style={styles.modalDate}>📅 {new Date(selectedEvent.event_date).toLocaleString()}</p>
            {selectedEvent.location && <p style={styles.modalDetail}>📍 {selectedEvent.location}</p>}
            {selectedEvent.description && <p style={styles.modalDesc}>{selectedEvent.description}</p>}
            {selectedEvent.link && (
              <a href={selectedEvent.link} target="_blank" rel="noopener noreferrer" style={styles.modalLink}>
                🔗 Join / View Link
              </a>
            )}
            <div style={styles.modalActions}>
              {isAdmin && (
                <button style={styles.deleteBtn} onClick={() => handleDeleteEvent(selectedEvent.id)}>
                  Delete Event
                </button>
              )}
              <button style={styles.closeBtn} onClick={() => setSelectedEvent(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== POLICIES ====================

const PoliciesTerms = ({ user }) => {
  const [policies, setPolicies] = useState([]);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', body: '', version: '1.0' });
  const [message, setMessage] = useState('');
  const isAdmin = user?.role_id === 1;

  useEffect(() => { fetchPolicies(); }, []);

  const fetchPolicies = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/academic/policies`);
      setPolicies(res.data.policies);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCreate = async () => {
    try {
      await axios.post(`${API_URL}/api/academic/policies`, form);
      setMessage('Policy created');
      setShowForm(false);
      setForm({ title: '', body: '', version: '1.0' });
      fetchPolicies();
    } catch (err) {
      setMessage('Error creating policy');
    }
  };

  const handleDelete = async (policyId) => {
    if (!window.confirm('Delete this policy?')) return;
    try {
      await axios.delete(`${API_URL}/api/academic/policies/${policyId}`);
      setSelectedPolicy(null);
      fetchPolicies();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div style={styles.policiesContainer}>
      <div style={styles.policiesList}>
        {isAdmin && (
          <button style={styles.addBtn} onClick={() => setShowForm(!showForm)}>+ Add Policy</button>
        )}
        {message && <p style={styles.successText}>{message}</p>}
        {showForm && (
          <div style={styles.policyForm}>
            <input style={styles.input} placeholder="Policy title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} />
            <input style={styles.input} placeholder="Version (e.g. 1.0)" value={form.version} onChange={e => setForm({...form, version: e.target.value})} />
            <textarea style={styles.textarea} placeholder="Policy content" value={form.body} onChange={e => setForm({...form, body: e.target.value})} rows={8} />
            <div style={styles.formActions}>
              <button style={styles.saveBtn} onClick={handleCreate}>Save Policy</button>
              <button style={styles.cancelBtn} onClick={() => setShowForm(false)}>Cancel</button>
            </div>
          </div>
        )}
        {policies.length === 0 && !showForm && <p style={styles.emptyText}>No policies posted yet.</p>}
        {policies.map(policy => (
          <div
            key={policy.id}
            style={selectedPolicy?.id === policy.id ? styles.policyItemActive : styles.policyItem}
            onClick={() => setSelectedPolicy(policy)}
          >
            <h4 style={styles.policyItemTitle}>{policy.title}</h4>
            <p style={styles.policyItemMeta}>Version {policy.version} — {new Date(policy.updated_at).toLocaleDateString()}</p>
          </div>
        ))}
      </div>
      <div style={styles.policyContent}>
        {!selectedPolicy && (
          <div style={styles.noPolicySelected}>
            <p style={styles.noConvText}>Select a policy to read</p>
          </div>
        )}
        {selectedPolicy && (
          <>
            <div style={styles.policyHeader}>
              <h3 style={styles.policyTitle}>{selectedPolicy.title}</h3>
              <p style={styles.policyMeta}>Version {selectedPolicy.version} — Last updated {new Date(selectedPolicy.updated_at).toLocaleDateString()}</p>
            </div>
            <div style={styles.policyBody}>
              {selectedPolicy.body.split('\n').map((line, i) => (
                <p key={i} style={styles.policyLine}>{line}</p>
              ))}
            </div>
            {isAdmin && (
              <button style={styles.deleteBtn} onClick={() => handleDelete(selectedPolicy.id)}>
                Delete Policy
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ==================== DOCUMENT CENTER ====================

const DocumentCenter = ({ user }) => {
  const [documents, setDocuments] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', file_url: '', category: '' });
  const [message, setMessage] = useState('');
  const isAdmin = user?.role_id <= 3;

  useEffect(() => { fetchDocuments(); }, []);

  const fetchDocuments = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/academic/documents`);
      setDocuments(res.data.documents);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCreate = async () => {
    try {
      await axios.post(`${API_URL}/api/academic/documents`, form);
      setMessage('Document added');
      setShowForm(false);
      setForm({ title: '', description: '', file_url: '', category: '' });
      fetchDocuments();
    } catch (err) {
      setMessage('Error adding document');
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('Remove this document?')) return;
    try {
      await axios.delete(`${API_URL}/api/academic/documents/${documentId}`);
      fetchDocuments();
    } catch (err) {
      console.error(err);
    }
  };

  const categories = [...new Set(documents.map(d => d.category).filter(Boolean))];

  const DocRow = ({ doc }) => (
    <div style={styles.docRow}>
      <div style={styles.docLeft}>
        <span style={styles.docIcon}>📄</span>
        <div>
          <p style={styles.docTitle}>{doc.title}</p>
          {doc.description && <p style={styles.docDesc}>{doc.description}</p>}
        </div>
      </div>
      <div style={styles.docRight}>
        <a href={doc.file_url} target="_blank" rel="noopener noreferrer" style={styles.downloadBtn}>
          Download
        </a>
        {isAdmin && (
          <button style={styles.deleteBtnSmall} onClick={() => handleDelete(doc.id)}>
            Remove
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div>
      {isAdmin && (
        <div style={styles.calendarActions}>
          <button style={styles.addBtn} onClick={() => setShowForm(!showForm)}>+ Add Document</button>
        </div>
      )}
      {message && <p style={styles.successText}>{message}</p>}
      {showForm && (
        <div style={styles.eventForm}>
          <input style={styles.input} placeholder="Document title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} />
          <input style={styles.input} placeholder="Description (optional)" value={form.description} onChange={e => setForm({...form, description: e.target.value})} />
          <input style={styles.input} placeholder="File URL" value={form.file_url} onChange={e => setForm({...form, file_url: e.target.value})} />
          <input style={styles.input} placeholder="Category (e.g. Workbooks, Forms)" value={form.category} onChange={e => setForm({...form, category: e.target.value})} />
          <div style={styles.formActions}>
            <button style={styles.saveBtn} onClick={handleCreate}>Save Document</button>
            <button style={styles.cancelBtn} onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </div>
      )}
      {documents.length === 0 && !showForm && <p style={styles.emptyText}>No documents posted yet.</p>}
      {categories.length > 0 ? (
        categories.map(category => (
          <div key={category} style={styles.docCategory}>
            <h3 style={styles.categoryTitle}>{category}</h3>
            {documents.filter(d => d.category === category).map(doc => (
              <DocRow key={doc.id} doc={doc} />
            ))}
          </div>
        ))
      ) : (
        documents.map(doc => <DocRow key={doc.id} doc={doc} />)
      )}
    </div>
  );
};

// ==================== STYLES ====================

const styles = {
  container: { minHeight: '100vh', backgroundColor: '#1A2744', color: '#ffffff' },
  header: { backgroundColor: '#0f1a33', borderBottom: '1px solid #C49A28', padding: '16px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  logo: { color: '#C49A28', fontSize: '24px', fontWeight: '800', margin: '0', letterSpacing: '2px' },
  logoText: { color: '#ffffff80', fontSize: '14px', letterSpacing: '1px' },
  headerRight: { display: 'flex', alignItems: 'center', gap: '16px' },
  welcome: { color: '#ffffff', fontSize: '14px' },
  returnBtn: { backgroundColor: 'transparent', border: '1px solid #C49A28', color: '#C49A28', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' },
  main: { maxWidth: '1200px', margin: '0 auto', padding: '32px' },
  pageHeader: { marginBottom: '24px' },
  pageTitle: { color: '#C49A28', fontSize: '28px', fontWeight: '700', margin: '0 0 4px 0' },
  pageSubtitle: { color: '#ffffff60', fontSize: '14px', margin: '0' },
  tabs: { display: 'flex', gap: '4px', marginBottom: '24px', borderBottom: '1px solid #ffffff15', flexWrap: 'wrap' },
  tab: { backgroundColor: 'transparent', border: 'none', color: '#ffffff60', padding: '12px 20px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', borderBottom: '2px solid transparent' },
  tabActive: { backgroundColor: 'transparent', border: 'none', color: '#C49A28', padding: '12px 20px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', borderBottom: '2px solid #C49A28' },
  content: { minHeight: '400px' },
  // Signable documents
  unsignedSection: { marginBottom: '40px' },
  unsignedTitle: { color: '#C49A28', fontSize: '18px', fontWeight: '700', margin: '0 0 4px 0' },
  unsignedSubtitle: { color: '#ffffff60', fontSize: '13px', margin: '0 0 20px 0' },
  unsignedCard: { backgroundColor: '#0f1a33', border: '2px solid #C49A2840', borderRadius: '12px', padding: '24px', marginBottom: '16px' },
  unsignedHeader: { marginBottom: '16px' },
  unsignedDocTitle: { color: '#ffffff', fontSize: '18px', fontWeight: '700', margin: '0 0 8px 0' },
  gateTag: { backgroundColor: '#C49A2820', color: '#C49A28', fontSize: '12px', padding: '4px 10px', borderRadius: '20px', fontWeight: '600' },
  docContentBox: { backgroundColor: '#1A2744', border: '1px solid #ffffff10', borderRadius: '8px', padding: '20px', marginBottom: '20px', maxHeight: '300px', overflowY: 'auto' },
  docContentLine: { color: '#ffffffcc', fontSize: '14px', lineHeight: '1.7', margin: '0 0 8px 0' },
  signArea: { backgroundColor: '#1A274460', border: '1px solid #C49A2840', borderRadius: '8px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' },
  signInstruction: { color: '#ffffff80', fontSize: '13px', margin: '0' },
  signInput: { backgroundColor: '#1A2744', border: '2px solid #C49A28', borderRadius: '6px', padding: '12px 16px', color: '#ffffff', fontSize: '15px', fontStyle: 'italic', outline: 'none', width: '100%', boxSizing: 'border-box' },
  signBtn: { backgroundColor: '#C49A28', color: '#1A2744', border: 'none', borderRadius: '6px', padding: '12px 24px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', alignSelf: 'flex-start' },
  signedSection: { marginTop: '32px' },
  signedTitle: { color: '#1E8449', fontSize: '16px', fontWeight: '700', margin: '0 0 16px 0' },
  signedCard: { backgroundColor: '#1E844910', border: '1px solid #1E844930', borderRadius: '8px', padding: '16px 20px', marginBottom: '10px' },
  signedLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  signedIcon: { fontSize: '20px' },
  signedDocTitle: { color: '#ffffff', fontSize: '15px', fontWeight: '600', margin: '0 0 4px 0' },
  signedMeta: { color: '#ffffff60', fontSize: '12px', margin: '0' },
  adminDocCard: { backgroundColor: '#0f1a33', border: '1px solid #ffffff15', borderRadius: '8px', padding: '16px 20px', marginBottom: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  adminDocLeft: { display: 'flex', flexDirection: 'column', gap: '4px' },
  adminDocTitle: { color: '#ffffff', fontSize: '15px', fontWeight: '600', margin: '0' },
  adminDocGate: { color: '#C49A28', fontSize: '12px', margin: '0' },
  adminDocStats: { color: '#ffffff60', fontSize: '12px', margin: '0' },
  sectionTitleGold: { color: '#C49A28', fontSize: '16px', fontWeight: '700', margin: '24px 0 12px 0' },
  successBanner: { backgroundColor: '#1E844920', border: '1px solid #1E844960', color: '#1E8449', borderRadius: '8px', padding: '12px 16px', marginBottom: '20px', fontSize: '14px' },
  errorBanner: { backgroundColor: '#ff000015', border: '1px solid #ff000040', color: '#ff6666', borderRadius: '8px', padding: '12px 16px', marginBottom: '20px', fontSize: '14px' },
  select: { width: '100%', backgroundColor: '#1A2744', border: '1px solid #ffffff20', borderRadius: '6px', padding: '10px 14px', color: '#ffffff', fontSize: '14px', outline: 'none', marginTop: '6px' },
  helpText: { color: '#ffffff40', fontSize: '12px', margin: '0' },
  saveBtnDisabled: { backgroundColor: '#ffffff20', color: '#ffffff40', border: 'none', borderRadius: '6px', padding: '10px 20px', fontSize: '13px', fontWeight: '700', cursor: 'not-allowed' },
  // Shared
  profileEditorCard: { backgroundColor: '#0f1a33', border: '1px solid #ffffff15', borderRadius: '10px', padding: '20px 24px', marginBottom: '24px' },
  profileEditorHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' },
  sectionTitle: { color: '#ffffff', fontSize: '16px', fontWeight: '600', margin: '0' },
  editBtn: { backgroundColor: '#1A6B8A', color: '#ffffff', border: 'none', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
  profileForm: { display: 'flex', flexDirection: 'column', gap: '10px' },
  profilesGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' },
  profileCard: { backgroundColor: '#0f1a33', border: '1px solid #ffffff15', borderRadius: '12px', padding: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', textAlign: 'center' },
  profilePhoto: { width: '80px', height: '80px', borderRadius: '50%', overflow: 'hidden' },
  photoImg: { width: '100%', height: '100%', objectFit: 'cover' },
  photoPlaceholder: { width: '80px', height: '80px', borderRadius: '50%', backgroundColor: '#1A6B8A', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ffffff', fontSize: '24px', fontWeight: '700' },
  profileInfo: { display: 'flex', flexDirection: 'column', gap: '4px' },
  profileName: { color: '#ffffff', fontSize: '16px', fontWeight: '600', margin: '0' },
  profileTitle: { color: '#C49A28', fontSize: '13px', margin: '0' },
  profileRole: { color: '#ffffff40', fontSize: '11px', margin: '0', textTransform: 'uppercase', letterSpacing: '1px' },
  profileBio: { color: '#ffffff80', fontSize: '13px', margin: '8px 0 0 0', lineHeight: '1.5' },
  dmBtn: { backgroundColor: '#1A6B8A', color: '#ffffff', border: 'none', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', marginTop: '8px' },
  messagesContainer: { display: 'grid', gridTemplateColumns: '280px 1fr', gap: '0', minHeight: '500px' },
  convList: { backgroundColor: '#0f1a33', borderRadius: '10px 0 0 10px', border: '1px solid #ffffff15', overflow: 'hidden' },
  convListTitle: { color: '#ffffff', fontSize: '14px', fontWeight: '600', padding: '16px', margin: '0', borderBottom: '1px solid #ffffff10' },
  convItem: { padding: '14px 16px', cursor: 'pointer', borderBottom: '1px solid #ffffff08', position: 'relative' },
  convItemActive: { padding: '14px 16px', cursor: 'pointer', borderBottom: '1px solid #ffffff08', backgroundColor: '#1A2744', position: 'relative' },
  convName: { color: '#ffffff', fontSize: '14px', fontWeight: '600', margin: '0 0 4px 0' },
  convPreview: { color: '#ffffff60', fontSize: '12px', margin: '0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  unreadBadge: { position: 'absolute', top: '14px', right: '16px', backgroundColor: '#C49A28', color: '#1A2744', borderRadius: '50%', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '700' },
  messageThread: { backgroundColor: '#0d1628', borderRadius: '0 10px 10px 0', border: '1px solid #ffffff15', borderLeft: 'none', display: 'flex', flexDirection: 'column' },
  noConvSelected: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  noConvText: { color: '#ffffff30', fontSize: '14px' },
  threadHeader: { padding: '16px 20px', borderBottom: '1px solid #ffffff10' },
  threadName: { color: '#ffffff', fontSize: '15px', fontWeight: '600', margin: '0' },
  messagesList: { flex: 1, padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '10px', overflowY: 'auto', minHeight: '300px', maxHeight: '400px' },
  messageSent: { alignSelf: 'flex-end', backgroundColor: '#C49A28', borderRadius: '12px 12px 0 12px', padding: '10px 14px', maxWidth: '70%' },
  messageReceived: { alignSelf: 'flex-start', backgroundColor: '#1A2744', border: '1px solid #ffffff15', borderRadius: '12px 12px 12px 0', padding: '10px 14px', maxWidth: '70%' },
  messageBody: { color: '#ffffff', fontSize: '14px', margin: '0 0 4px 0', lineHeight: '1.4' },
  messageTime: { color: '#ffffff60', fontSize: '11px', margin: '0' },
  messageInput: { padding: '16px 20px', borderTop: '1px solid #ffffff10', display: 'flex', gap: '10px' },
  messageField: { flex: 1, backgroundColor: '#1A2744', border: '1px solid #ffffff20', borderRadius: '6px', padding: '10px 14px', color: '#ffffff', fontSize: '14px', outline: 'none' },
  sendBtn: { backgroundColor: '#C49A28', color: '#1A2744', border: 'none', borderRadius: '6px', padding: '10px 20px', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
  calendarActions: { display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' },
  calendar: { backgroundColor: '#0f1a33', border: '1px solid #ffffff15', borderRadius: '10px', overflow: 'hidden' },
  calendarHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', borderBottom: '1px solid #ffffff10' },
  calNavBtn: { backgroundColor: 'transparent', border: '1px solid #ffffff20', color: '#ffffff', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '16px' },
  monthTitle: { color: '#C49A28', fontSize: '18px', fontWeight: '700', margin: '0' },
  calendarGrid: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '1px', backgroundColor: '#ffffff10' },
  dayHeader: { backgroundColor: '#0f1a33', color: '#ffffff60', fontSize: '12px', fontWeight: '600', padding: '10px', textAlign: 'center' },
  dayCell: { backgroundColor: '#0f1a33', minHeight: '80px', padding: '8px', position: 'relative' },
  emptyCell: { backgroundColor: '#0a1220', minHeight: '80px' },
  dayNumber: { color: '#ffffff60', fontSize: '13px', display: 'block', marginBottom: '4px' },
  eventDot: { backgroundColor: '#C49A2830', color: '#C49A28', fontSize: '10px', padding: '2px 6px', borderRadius: '4px', marginBottom: '2px', cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: '#00000080', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { backgroundColor: '#1A2744', border: '1px solid #C49A28', borderRadius: '12px', padding: '32px', maxWidth: '500px', width: '90%' },
  modalTitle: { color: '#C49A28', fontSize: '20px', fontWeight: '700', margin: '0 0 12px 0' },
  modalDate: { color: '#ffffff', fontSize: '14px', margin: '0 0 8px 0' },
  modalDetail: { color: '#ffffff80', fontSize: '14px', margin: '0 0 8px 0' },
  modalDesc: { color: '#ffffff80', fontSize: '14px', margin: '12px 0', lineHeight: '1.6' },
  modalLink: { color: '#1A6B8A', fontSize: '14px', fontWeight: '600', display: 'block', marginBottom: '16px' },
  modalActions: { display: 'flex', gap: '10px', justifyContent: 'flex-end' },
  policiesContainer: { display: 'grid', gridTemplateColumns: '260px 1fr', gap: '0', minHeight: '500px' },
  policiesList: { backgroundColor: '#0f1a33', border: '1px solid #ffffff15', borderRadius: '10px 0 0 10px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '8px' },
  policyItem: { padding: '12px', borderRadius: '6px', cursor: 'pointer', border: '1px solid transparent' },
  policyItemActive: { padding: '12px', borderRadius: '6px', cursor: 'pointer', border: '1px solid #C49A28', backgroundColor: '#C49A2810' },
  policyItemTitle: { color: '#ffffff', fontSize: '13px', fontWeight: '600', margin: '0 0 4px 0' },
  policyItemMeta: { color: '#ffffff40', fontSize: '11px', margin: '0' },
  policyContent: { backgroundColor: '#0d1628', border: '1px solid #ffffff15', borderLeft: 'none', borderRadius: '0 10px 10px 0', padding: '24px' },
  noPolicySelected: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' },
  policyHeader: { marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid #ffffff10' },
  policyTitle: { color: '#C49A28', fontSize: '20px', fontWeight: '700', margin: '0 0 4px 0' },
  policyMeta: { color: '#ffffff40', fontSize: '12px', margin: '0' },
  policyBody: { display: 'flex', flexDirection: 'column', gap: '8px' },
  policyLine: { color: '#ffffffcc', fontSize: '14px', lineHeight: '1.7', margin: '0' },
  policyForm: { display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '12px' },
  docCategory: { marginBottom: '24px' },
  categoryTitle: { color: '#C49A28', fontSize: '15px', fontWeight: '700', margin: '0 0 12px 0', paddingBottom: '8px', borderBottom: '1px solid #ffffff10' },
  docRow: { backgroundColor: '#0f1a33', border: '1px solid #ffffff10', borderRadius: '8px', padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' },
  docLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  docIcon: { fontSize: '20px' },
  docTitle: { color: '#ffffff', fontSize: '14px', fontWeight: '600', margin: '0 0 2px 0' },
  docDesc: { color: '#ffffff60', fontSize: '12px', margin: '0' },
  docRight: { display: 'flex', gap: '8px', alignItems: 'center' },
  downloadBtn: { backgroundColor: '#1A6B8A', color: '#ffffff', padding: '6px 14px', borderRadius: '6px', fontSize: '13px', fontWeight: '600', textDecoration: 'none' },
  deleteBtnSmall: { backgroundColor: 'transparent', border: '1px solid #ff000030', color: '#ff6666', padding: '6px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' },
  input: { width: '100%', backgroundColor: '#1A2744', border: '1px solid #ffffff20', borderRadius: '6px', padding: '10px 14px', color: '#ffffff', fontSize: '14px', outline: 'none', boxSizing: 'border-box' },
  textarea: { width: '100%', backgroundColor: '#1A2744', border: '1px solid #ffffff20', borderRadius: '6px', padding: '10px 14px', color: '#ffffff', fontSize: '14px', outline: 'none', resize: 'vertical', boxSizing: 'border-box' },
  checkboxLabel: { display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' },
  checkboxText: { color: '#ffffff80', fontSize: '13px' },
  saveBtn: { backgroundColor: '#C49A28', color: '#1A2744', border: 'none', borderRadius: '6px', padding: '10px 20px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  cancelBtn: { backgroundColor: 'transparent', border: '1px solid #ffffff30', color: '#ffffff60', borderRadius: '6px', padding: '10px 20px', fontSize: '13px', cursor: 'pointer' },
  addBtn: { backgroundColor: '#C49A28', color: '#1A2744', border: 'none', borderRadius: '6px', padding: '10px 20px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  deleteBtn: { backgroundColor: 'transparent', border: '1px solid #ff000040', color: '#ff6666', borderRadius: '6px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer' },
  closeBtn: { backgroundColor: '#1A6B8A', color: '#ffffff', border: 'none', borderRadius: '6px', padding: '8px 16px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  formRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  formGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
  fieldLabel: { color: '#ffffff60', fontSize: '12px' },
  formActions: { display: 'flex', gap: '8px' },
  eventForm: { backgroundColor: '#0f1a33', border: '1px solid #ffffff15', borderRadius: '10px', padding: '20px', marginBottom: '24px', display: 'flex', flexDirection: 'column', gap: '10px' },
  loadingText: { color: '#ffffff60', fontSize: '14px' },
  emptyState: { textAlign: 'center', padding: '60px', backgroundColor: '#0f1a33', borderRadius: '10px', border: '1px solid #ffffff10' },
  emptyText: { color: '#ffffff30', fontSize: '14px' },
  emptyTextSmall: { color: '#ffffff30', fontSize: '13px', padding: '16px' },
  successText: { color: '#1E8449', fontSize: '13px', margin: '4px 0' },
};

export default AcademicResourceCenter;